import { useState, useEffect } from "react";
import { X, Eye, Type, MousePointer, Contrast, ZoomIn, Pause } from "lucide-react";

const DEFAULTS = { fontSize: 0, highContrast: false, noAnimations: false, bigCursor: false, textSpacing: false, highlightLinks: false };

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(() => {
    try { return { ...DEFAULTS, ...JSON.parse(localStorage.getItem('ugl_a11y') || '{}') }; } catch { return DEFAULTS; }
  });

  useEffect(() => {
    localStorage.setItem('ugl_a11y', JSON.stringify(settings));
    const root = document.documentElement;

    // Font size
    root.style.fontSize = settings.fontSize === 0 ? '' : `${100 + settings.fontSize * 15}%`;

    // High contrast
    root.classList.toggle('a11y-high-contrast', settings.highContrast);

    // No animations
    root.classList.toggle('a11y-no-animations', settings.noAnimations);

    // Big cursor
    root.classList.toggle('a11y-big-cursor', settings.bigCursor);

    // Text spacing
    root.classList.toggle('a11y-text-spacing', settings.textSpacing);

    // Highlight links
    root.classList.toggle('a11y-highlight-links', settings.highlightLinks);
  }, [settings]);

  const update = (key, val) => setSettings(prev => ({ ...prev, [key]: val }));
  const reset = () => setSettings(DEFAULTS);
  const isModified = JSON.stringify(settings) !== JSON.stringify(DEFAULTS);

  return (
    <>
      {/* Accessibility Icon Button */}
      <button
        onClick={() => setOpen(!open)}
        data-testid="a11y-toggle"
        aria-label="Apri pannello accessibilità"
        className="fixed bottom-24 right-6 z-[95] w-12 h-12 rounded-full bg-[#003366] border-2 border-white/20 flex items-center justify-center shadow-lg shadow-black/30 hover:scale-110 hover:bg-[#004488] transition-all duration-200"
      >
        {/* Universal accessibility icon */}
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="4.5" r="2.5" />
          <path d="M12 7v5" />
          <path d="M8 9h8" />
          <path d="M9 22l3-8 3 8" />
        </svg>
        {isModified && <div className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#C9A84C] border border-white" />}
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed bottom-24 right-20 z-[96] w-80 bg-[#13243d] border border-[#1e3456] rounded-2xl shadow-2xl shadow-black/40 overflow-hidden" data-testid="a11y-panel" role="dialog" aria-label="Impostazioni accessibilità">
          {/* Header */}
          <div className="bg-[#003366] p-4 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="4.5" r="2.5" /><path d="M12 7v5" /><path d="M8 9h8" /><path d="M9 22l3-8 3 8" />
              </svg>
              <span className="text-sm font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Accessibilità</span>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/60 hover:text-white p-1" aria-label="Chiudi pannello">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Options */}
          <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
            {/* Font Size */}
            <div className="bg-[#0f1d32] rounded-xl p-3 border border-[#1e3456]">
              <div className="flex items-center gap-2 mb-2">
                <Type className="w-4 h-4 text-[#5ba8d9]" />
                <span className="text-xs font-medium text-white">Dimensione Testo</span>
              </div>
              <div className="flex gap-1.5">
                {[-1, 0, 1, 2, 3].map(v => (
                  <button key={v} onClick={() => update('fontSize', v)}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-all ${settings.fontSize === v ? 'bg-[#5ba8d9] text-white' : 'bg-[#1a3a5c] text-white/40 hover:text-white/70'}`}
                    aria-label={`Dimensione testo ${v === -1 ? 'piccola' : v === 0 ? 'normale' : v === 1 ? 'grande' : v === 2 ? 'molto grande' : 'massima'}`}>
                    {v === -1 ? 'A⁻' : v === 0 ? 'A' : v === 1 ? 'A⁺' : v === 2 ? 'A⁺⁺' : 'A⁺⁺⁺'}
                  </button>
                ))}
              </div>
            </div>

            {/* Toggle Options */}
            {[
              { key: 'highContrast', icon: Contrast, label: 'Alto Contrasto', desc: 'Aumenta contrasto testi e bordi' },
              { key: 'noAnimations', icon: Pause, label: 'Disattiva Animazioni', desc: 'Ferma tutte le animazioni' },
              { key: 'bigCursor', icon: MousePointer, label: 'Cursore Grande', desc: 'Ingrandisci il puntatore del mouse' },
              { key: 'textSpacing', icon: Type, label: 'Spaziatura Testo', desc: 'Aumenta lo spazio tra le righe' },
              { key: 'highlightLinks', icon: Eye, label: 'Evidenzia Link', desc: 'Sottolinea e colora tutti i link' },
            ].map(opt => (
              <button key={opt.key} onClick={() => update(opt.key, !settings[opt.key])}
                className={`w-full bg-[#0f1d32] rounded-xl p-3 border text-left flex items-center gap-3 transition-all ${settings[opt.key] ? 'border-[#C9A84C] bg-[#C9A84C]/5' : 'border-[#1e3456] hover:border-[#2a5080]'}`}
                aria-pressed={settings[opt.key]}
                data-testid={`a11y-${opt.key}`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${settings[opt.key] ? 'bg-[#C9A84C]/20' : 'bg-[#1a3a5c]'}`}>
                  <opt.icon className={`w-4 h-4 ${settings[opt.key] ? 'text-[#C9A84C]' : 'text-white/40'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium ${settings[opt.key] ? 'text-[#C9A84C]' : 'text-white'}`}>{opt.label}</p>
                  <p className="text-[10px] text-white/30">{opt.desc}</p>
                </div>
                <div className={`w-8 h-4 rounded-full flex items-center transition-all ${settings[opt.key] ? 'bg-[#C9A84C] justify-end' : 'bg-[#1e3456] justify-start'}`}>
                  <div className={`w-3 h-3 rounded-full mx-0.5 ${settings[opt.key] ? 'bg-white' : 'bg-white/30'}`} />
                </div>
              </button>
            ))}
          </div>

          {/* Footer */}
          {isModified && (
            <div className="p-3 border-t border-[#1e3456]">
              <button onClick={reset} className="w-full text-center text-xs text-white/40 hover:text-white/70 py-1.5 transition-colors" data-testid="a11y-reset">
                Ripristina impostazioni predefinite
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
